import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/endpoints';
import { useDataVersion } from '../hooks/useDataVersion';
import type {
  CreditAdjustment,
  CustomerTab as Tab,
  FnbItem,
  PaymentMode,
  TabLine,
  TabPayment,
} from '../api/types';
import {
  formatDate,
  formatDateTime,
  formatMinutes,
  creditLabel,
  formatMoney,
  owedAfterCredit,
  POOL_LABEL,
} from '../lib/format';
import { useToast } from './Toast';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  Spinner,
} from './ui';

// The menu laid out like the kitchens' printed menus, the same way the
// station screen shows it: a heading per section (Frankies, Pizza, ...), a card
// per dish, and the dish's variants — "Classic Veg Frankie" and "Classic Veg
// Frankie (Cheese)" — as price buttons on one card. Staff tap a price and that
// item lands on the tab at its current selling price; they never type a price.
// Used from the customer card and from the floor panel while the customer is
// playing.
type DishOption = { item: FnbItem; label: string | null };
type Dish = { key: string; name: string; options: DishOption[] };
type MenuSection = { key: string; name: string; vendorName: string | null; icon: string; dishes: Dish[] };

const OTHER_SECTION = 'Other';
const REGULAR = 'Regular';

/** "Classic Veg Frankie (Cheese)" -> ["Classic Veg Frankie", "Cheese"]. The
 *  admin names a variant by putting it in brackets; that is the whole rule,
 *  shared with the station menu. */
function splitVariant(name: string): [string, string | null] {
  const match = /^(.*\S)\s*\(([^()]+)\)\s*$/.exec(name);
  return match ? [match[1], match[2].trim()] : [name, null];
}

// Found from the section's name, so a new vendor's "Rolls" or "Shakes" gets
// one without anyone configuring it. Same table as the station menu.
const SECTION_ICONS: [RegExp, string][] = [
  [/frank|roll|wrap|kathi/i, '🌯'],
  [/maggi|noodle|ramen/i, '🍜'],
  [/momo|dumpling|dim ?sum/i, '🥟'],
  [/fries|chips/i, '🍟'],
  [/pizza/i, '🍕'],
  [/burger|combo/i, '🍔'],
  [/pasta|spaghetti|mac/i, '🍝'],
  [/sandwich|grill|toast|sub/i, '🥪'],
  [/dip|sauce/i, '🥣'],
  [/bowl|rice|biryani/i, '🍛'],
  [/coffee|tea|chai/i, '☕'],
  [/shake|beverage|drink|juice|soda|cold/i, '🥤'],
  [/dessert|sweet|ice ?cream|brownie|cake/i, '🍨'],
  [/snack|starter|nachos/i, '🍿'],
];
const sectionIcon = (section: string) =>
  SECTION_ICONS.find(([pattern]) => pattern.test(section))?.[1] ?? '🍽️';

function buildSections(items: FnbItem[]): MenuSection[] {
  const sections: MenuSection[] = [];
  // The server sorts by vendor, category, name; grouping keeps that order.
  for (const item of items) {
    const sectionName = item.category?.trim() || OTHER_SECTION;
    const sectionKey = `${item.vendor_id ?? ''}|${sectionName}`;
    let section = sections.find((entry) => entry.key === sectionKey);
    if (!section) {
      section = {
        key: sectionKey,
        name: sectionName,
        vendorName: item.vendor_name,
        icon: sectionIcon(sectionName),
        dishes: [],
      };
      sections.push(section);
    }
    const [base, variant] = splitVariant(item.name);
    const dishKey = base.toLowerCase();
    let dish = section.dishes.find((entry) => entry.key === dishKey);
    if (!dish) {
      dish = { key: dishKey, name: base, options: [] };
      section.dishes.push(dish);
    }
    dish.options.push({ item, label: variant ?? REGULAR });
  }

  for (const section of sections) {
    for (const dish of section.dishes) {
      if (dish.options.length === 1) {
        // Nothing to choose between: keep the full name — for "Crispy Veg
        // Momos (5 pcs)" the bracket is information, not a variant.
        dish.name = dish.options[0].item.name;
        dish.options[0].label = null;
      } else {
        dish.options.sort(
          (a, b) =>
            Number(b.label === REGULAR) - Number(a.label === REGULAR) ||
            a.item.selling_price - b.item.selling_price,
        );
      }
    }
  }
  return sections;
}

export function AddItemDialog({
  customerId,
  customerName,
  onClose,
  onAdded,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onAdded: (tab: Tab) => void;
}) {
  const toast = useToast();
  return (
    <MenuPickerDialog
      title={`Add to ${customerName}'s tab`}
      priceOf={(item) => item.selling_price}
      onClose={onClose}
      onPick={async (item) => {
        const tab = await api.customers.addToTab(customerId, item.item_id, 1);
        toast.success(`${item.name} added. ${customerName} owes ${formatMoney(tab.due_amount)}.`);
        onAdded(tab);
      }}
    />
  );
}

// The menu picker itself, shared by customer and staff tabs. `priceOf` is the
// price shown on each button (undefined = not known to this user, so the
// button just says Add); `onPick` puts the item on the tab and throws the
// backend's message on failure.
export function MenuPickerDialog({
  title,
  priceOf,
  onPick,
  onClose,
}: {
  title: string;
  priceOf: (item: FnbItem) => number | undefined;
  onPick: (item: FnbItem) => Promise<void>;
  onClose: () => void;
}) {
  const [items, setItems] = useState<FnbItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // Which kitchen's items to show; null = all of them.
  const [vendor, setVendor] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api.fnb
      .items(controller.signal)
      .then(setItems)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, []);

  const vendors = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of items ?? []) {
      seen.set(item.vendor_id ?? '', item.vendor_name ?? 'Counter');
    }
    return [...seen];
  }, [items]);

  const sections = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!items) return [];
    return buildSections(
      items.filter(
        (item) =>
          (vendor === null || (item.vendor_id ?? '') === vendor) &&
          (!needle ||
            item.name.toLowerCase().includes(needle) ||
            (item.category ?? '').toLowerCase().includes(needle)),
      ),
    );
  }, [items, filter, vendor]);

  const add = async (item: FnbItem) => {
    setBusyItem(item.item_id);
    setError(null);
    try {
      await onPick(item);
      // Stay open: an order is usually more than one thing.
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyItem(null);
    }
  };

  // With every kitchen showing, say whose section it is.
  const showVendor = vendor === null && vendors.length > 1;

  return (
    <Modal title={title} onClose={onClose} wide>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        {items === null && !error ? (
          <Spinner label="Loading the menu…" />
        ) : items && items.length === 0 ? (
          <EmptyState title="The menu is empty.">
            An admin needs to add items under the Menu tab first.
          </EmptyState>
        ) : (
          <>
            {items && items.length > 8 && (
              <Input
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Search dishes or sections…"
                autoFocus
              />
            )}
            {vendors.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {[[null, 'All'] as [string | null, string], ...vendors].map(([id, label]) => (
                  <button
                    key={id ?? 'all'}
                    type="button"
                    onClick={() => setVendor(id)}
                    className={`min-h-9 rounded-full border px-3 text-sm transition-colors ${
                      vendor === id
                        ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                        : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {sections.map((section) => (
              <section key={section.key} className="space-y-2">
                <h3 className="flex items-baseline gap-2 text-sm font-semibold uppercase tracking-wide text-slate-300">
                  <span aria-hidden="true">{section.icon}</span>
                  {section.name}
                  {showVendor && (
                    <span className="text-xs font-normal normal-case tracking-normal text-slate-500">
                      {section.vendorName ?? 'Counter'}
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {section.dishes.map((dish) => (
                    <div
                      key={dish.key}
                      className="flex flex-col justify-between gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3"
                    >
                      <span className="font-medium text-slate-100">{dish.name}</span>
                      <div className="flex flex-wrap gap-2">
                        {dish.options.map(({ item, label }) => {
                          const price = priceOf(item);
                          return (
                            <button
                              key={item.item_id}
                              type="button"
                              disabled={busyItem !== null}
                              onClick={() => add(item)}
                              aria-label={price === undefined ? `Add ${item.name}` : `Add ${item.name}, ${formatMoney(price)}`}
                              className="flex min-h-11 flex-1 items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-950 px-3 text-sm transition-colors hover:border-sky-500/60 hover:bg-sky-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {label && <span className="text-slate-400">{label}</span>}
                              <span className="font-medium text-sky-300">
                                {busyItem === item.item_id
                                  ? 'Adding…'
                                  : price === undefined
                                    ? 'Add'
                                    : formatMoney(price)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
            {sections.length === 0 && (
              <p className="py-4 text-center text-sm text-slate-500">
                Nothing on the menu matches "{filter}".
              </p>
            )}
          </>
        )}

        <Button variant="secondary" className="w-full" onClick={onClose}>
          Done
        </Button>
      </div>
    </Modal>
  );
}

// Money typed as digits and an optional point; stays a string until submit.
const cleanMoney = (value: string) => value.replace(/[^0-9.]/g, '');
const roundMoney = (value: number) => Math.round(value * 100) / 100;

const owed = (tab: Tab) => owedAfterCredit(tab.due_amount, tab.credit_balance);

// Cash or UPI is one tap; a split asks how much went each way.
export function PaymentModePicker({
  mode,
  onChange,
}: {
  mode: PaymentMode;
  onChange: (mode: PaymentMode) => void;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-sm font-medium text-slate-300">Paid by</span>
      <div className="grid grid-cols-3 gap-2">
        {(['cash', 'upi', 'split'] as PaymentMode[]).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={`min-h-11 rounded-lg border text-sm font-medium uppercase transition-colors ${
              mode === option
                ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                : 'border-slate-800 text-slate-300 hover:bg-slate-800'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

// For a split, staff type the cash part and the UPI part is the rest — one
// field to fill, and the two can never fail to add up.
export function SplitFields({
  cashPart,
  onCashPart,
  upiPart,
}: {
  cashPart: string;
  onCashPart: (value: string) => void;
  upiPart: number;
}) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Input
        label="Cash part (₹)"
        value={cashPart}
        onChange={(event) => onCashPart(cleanMoney(event.target.value))}
        inputMode="decimal"
        placeholder="0"
      />
      <div>
        <span className="mb-1.5 block text-sm font-medium text-slate-300">UPI part</span>
        <p
          className={`flex min-h-11 items-center rounded-lg border border-slate-800 bg-slate-950 px-3 text-base ${
            upiPart < 0 ? 'text-rose-300' : 'text-slate-100'
          }`}
        >
          {upiPart < 0 ? 'Cash part is more than received' : formatMoney(upiPart)}
        </p>
      </div>
    </div>
  );
}

// End of the chosen day, local time: "settle by Friday" means Friday night,
// not Friday 00:00.
const endOfDay = (date: string) => new Date(`${date}T23:59:59`).toISOString();
const daysFromToday = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const SETTLE_PRESETS: { label: string; days: number }[] = [
  { label: 'Tonight', days: 0 },
  { label: '3 days', days: 3 },
  { label: '1 week', days: 7 },
  { label: '1 month', days: 30 },
];

function SettleByPicker({ date, onChange }: { date: string; onChange: (date: string) => void }) {
  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        {SETTLE_PRESETS.map((preset) => {
          const value = daysFromToday(preset.days);
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => onChange(value)}
              className={`min-h-11 rounded-lg border text-sm font-medium transition-colors ${
                date === value
                  ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                  : 'border-slate-800 text-slate-300 hover:bg-slate-800'
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <Input
        label="Settle by"
        type="date"
        value={date}
        min={daysFromToday(0)}
        onChange={(event) => onChange(event.target.value)}
      />
    </>
  );
}

// "Mark paid" gets a confirmation because it is the one irreversible action on
// this screen — the lines move into a settled bill and cannot be removed after.
// `lines` is what is being settled: the whole open tab, or the one line whose
// "Paid" button was tapped.
//
// Stored credit is used first, automatically. The amount-received field is
// what the customer hands over: more than needed is kept as credit ("keep the
// change"). Less settles the lines only when staff say so explicitly — the
// difference then goes back on the tab as a balance owed, so ₹1,350 against a
// ₹1,355 bill is recordable without a typo silently becoming a debt.
function PayDialog({
  tab,
  lines,
  onClose,
  onPaid,
}: {
  tab: Tab;
  lines: TabLine[];
  onClose: () => void;
  onPaid: (tab: Tab) => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [received, setReceived] = useState('');
  // Cash or UPI is one tap; a split asks how much went each way.
  const [mode, setMode] = useState<PaymentMode>('cash');
  const [cashPart, setCashPart] = useState('');
  const [carryBalance, setCarryBalance] = useState(false);

  const everything = lines.length === tab.unpaid.length;
  const amount = lines.reduce((sum, line) => sum + line.line_total, 0);
  const creditApplied = Math.min(tab.credit_balance, amount);
  const cashNeeded = Math.round((amount - creditApplied) * 100) / 100;
  const receivedValue = received.trim() === '' ? cashNeeded : Number(received);
  const validAmount = Number.isFinite(receivedValue) && receivedValue >= 0;
  const short = validAmount && receivedValue + 0.005 < cashNeeded;
  const creditAdded = validAmount && !short ? Math.round((receivedValue - cashNeeded) * 100) / 100 : 0;
  const shortBy = short ? Math.round((cashNeeded - receivedValue) * 100) / 100 : 0;
  const blockedByShort = short && !carryBalance;
  // For a split, staff type the cash part and the UPI part is the rest — one
  // field to fill, and the two can never fail to add up.
  const cashPartValue = cashPart.trim() === '' ? 0 : Number(cashPart);
  const upiPartValue = Math.round((receivedValue - cashPartValue) * 100) / 100;
  const splitValid = mode !== 'split' || (cashPartValue >= 0 && upiPartValue >= 0 && cashPart.trim() !== '');

  const pay = async () => {
    if (!validAmount || blockedByShort || !splitValid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.customers.payTab(tab.customer_id, {
        payment_mode: mode,
        tab_item_ids: everything ? undefined : lines.map((line) => line.tab_item_id),
        amount_received: received.trim() === '' ? undefined : receivedValue,
        ...(short ? { carry_balance: true } : {}),
        ...(mode === 'split' ? { cash_amount: cashPartValue, upi_amount: upiPartValue } : {}),
      });
      // The server totals the lines at the moment of payment, so if a colleague
      // added something in between the amount here may differ from the one on
      // screen. Its message says what was actually taken — show that.
      const bits = [`${formatMoney(result.payment.amount_received)} received from ${tab.customer_name}.`];
      if (result.payment.credit_applied > 0)
        bits.push(`${formatMoney(result.payment.credit_applied)} taken from credit.`);
      if (result.payment.credit_added > 0)
        bits.push(`${formatMoney(result.payment.credit_added)} kept as credit.`);
      if ((result.payment.balance_carried ?? 0) > 0)
        bits.push(`${formatMoney(result.payment.balance_carried ?? 0)} left owed on the tab.`);
      bits.push(result.tab.due_amount > 0 ? `${formatMoney(result.tab.due_amount)} still due.` : 'Tab cleared.');
      toast.success(bits.join(' '));
      onPaid(result.tab);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const count = lines.reduce((sum, line) => sum + line.qty, 0);

  return (
    <Modal title={everything ? 'Mark tab as paid' : 'Mark item as paid'} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="rounded-xl border border-slate-800 bg-slate-950 p-5 text-center">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            Collect from {tab.customer_name}
          </p>
          <p className="mt-1 text-4xl font-semibold text-emerald-300">
            {formatMoney(cashNeeded)}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {count} item{count === 1 ? '' : 's'} · {formatMoney(amount)}
            {creditApplied > 0 && ` − ${formatMoney(creditApplied)} credit`}
            {!everything && ` · ${formatMoney(tab.due_amount - amount)} stays on the tab`}
          </p>
        </div>

        <ul className="divide-y divide-slate-800 text-sm">
          {lines.map((line) => (
            <li key={line.tab_item_id} className="flex justify-between py-1.5">
              <span className="text-slate-300">
                {line.kind === 'recharge' && <span className="text-sky-400">Recharge · </span>}
                {line.kind === 'ps5' && <span className="text-violet-400">PS5 · </span>}
                {line.kind === 'balance' && <span className="text-amber-400">Owed · </span>}
                {line.item_name}
                {line.qty > 1 && <span className="text-slate-500"> ×{line.qty}</span>}
              </span>
              <span className="text-slate-200">{formatMoney(line.line_total)}</span>
            </li>
          ))}
        </ul>

        <PaymentModePicker mode={mode} onChange={setMode} />

        <Input
          label="Amount received (₹)"
          value={received}
          onChange={(event) => setReceived(cleanMoney(event.target.value))}
          inputMode="decimal"
          placeholder={String(cashNeeded)}
          hint={
            short
              ? `That is ${formatMoney(shortBy)} short.`
              : creditAdded > 0
                ? `${formatMoney(creditAdded)} over — it will be kept as ${tab.customer_name}'s credit for next time.`
                : 'Leave blank for the exact amount. More than that is kept as credit.'
          }
        />

        {/* Short: either take the full amount, or say out loud that the
            difference stays owed. Never a silent write-off, never a silent
            debt. */}
        {short && (
          <label
            className={`flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
              carryBalance
                ? 'border-amber-500/60 bg-amber-500/10 text-amber-100'
                : 'border-slate-700 text-slate-300'
            }`}
          >
            <input
              type="checkbox"
              checked={carryBalance}
              onChange={(event) => setCarryBalance(event.target.checked)}
              className="mt-0.5 h-5 w-5 shrink-0 accent-amber-500"
            />
            <span>
              Mark {count === 1 ? 'it' : 'them'} paid and leave{' '}
              <span className="font-semibold">{formatMoney(shortBy)}</span> owed on{' '}
              {tab.customer_name}'s tab
              <span className="block text-xs text-slate-400">
                Otherwise take the full amount, or mark fewer items paid.
              </span>
            </span>
          </label>
        )}

        {mode === 'split' && (
          <SplitFields cashPart={cashPart} onCashPart={setCashPart} upiPart={upiPartValue} />
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            className="flex-1"
            disabled={busy || !validAmount || blockedByShort || !splitValid}
            onClick={pay}
          >
            {busy
              ? 'Recording…'
              : `Received ${formatMoney(validAmount ? receivedValue : cashNeeded)} ${
                  mode === 'split' ? '(split)' : mode === 'upi' ? 'by UPI' : 'in cash'
                }${short && carryBalance ? ` · ${formatMoney(shortBy)} owed` : ''}`}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Not yet
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// The settle-by date. A regular who pays weekly, a group settling at the end
// of the night — recorded so the next shift knows, and flagged once it passes.
function KeepOpenDialog({
  tab,
  onClose,
  onSaved,
}: {
  tab: Tab;
  onClose: () => void;
  onSaved: (tab: Tab) => void;
}) {
  const toast = useToast();
  const [date, setDate] = useState(
    tab.tab_open_until ? tab.tab_open_until.slice(0, 10) : '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (clear = false) => {
    setBusy(true);
    setError(null);
    try {
      const until = clear || !date ? null : endOfDay(date);
      const next = await api.customers.keepTabOpen(tab.customer_id, until);
      toast.success(
        until
          ? `${tab.customer_name}'s tab stays open until ${formatDate(until)}.`
          : `${tab.customer_name}'s tab no longer has a settle-by date.`,
      );
      onSaved(next);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Keep the tab open" onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-sm text-slate-300">
          Record when {tab.customer_name} will settle. The balance stays due
          either way; past this date it is flagged as overdue.
        </p>
        <SettleByPicker date={date} onChange={setDate} />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="flex-1" disabled={busy || !date} onClick={() => save()}>
            {busy ? 'Saving…' : 'Keep open'}
          </Button>
          {tab.tab_open_until && (
            <Button variant="secondary" className="flex-1" disabled={busy} onClick={() => save(true)}>
              Clear date
            </Button>
          )}
          <Button variant="ghost" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// One line as it appears on the bill. Food and drinks are described by the
// menu; a recharge says what pack it is and what is left on it.
const MODE_LABEL: Record<PaymentMode, string> = { cash: 'cash', upi: 'UPI', split: 'split' };

// Fixes how a recorded payment arrived — cash tapped for a UPI payment, or a
// split keyed the wrong way round. Admin-only. The amount is fixed: only the
// mode and the cash/UPI division move, so the drawer and the UPI statement
// reconcile without touching what the customer paid for.
function ChangeModeDialog({
  tab,
  payment,
  onClose,
  onSaved,
}: {
  tab: Tab;
  payment: TabPayment;
  onClose: () => void;
  onSaved: (tab: Tab) => void;
}) {
  const toast = useToast();
  const received = payment.amount_received;
  const [mode, setMode] = useState<PaymentMode>(payment.payment_mode ?? 'cash');
  const [cashPart, setCashPart] = useState(
    payment.payment_mode === 'split' ? String(payment.cash_amount) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cashPartValue = cashPart.trim() === '' ? 0 : Number(cashPart);
  const upiPartValue = roundMoney(received - cashPartValue);
  const splitValid =
    mode !== 'split' || (cashPart.trim() !== '' && cashPartValue >= 0 && upiPartValue >= 0);
  const unchanged =
    mode === payment.payment_mode &&
    (mode !== 'split' || Math.abs(cashPartValue - payment.cash_amount) < 0.005);

  const save = async () => {
    if (!splitValid || unchanged) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.customers.changePaymentMode(tab.customer_id, payment.payment_id, {
        payment_mode: mode,
        ...(mode === 'split' ? { cash_amount: cashPartValue, upi_amount: upiPartValue } : {}),
      });
      toast.success(
        `${formatMoney(received)} payment now recorded as ` +
          (mode === 'split'
            ? `${formatMoney(cashPartValue)} cash + ${formatMoney(upiPartValue)} UPI.`
            : `${MODE_LABEL[mode]}.`),
      );
      onSaved(result.tab);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Change payment mode" onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
          <p className="text-2xl font-semibold text-slate-100">{formatMoney(received)}</p>
          <p className="text-xs text-slate-500">
            Received {formatDateTime(payment.paid_at)}
            {payment.received_by_name && ` · ${payment.received_by_name}`} · recorded as{' '}
            {payment.payment_mode === 'split'
              ? `${formatMoney(payment.cash_amount)} cash + ${formatMoney(payment.upi_amount)} UPI`
              : payment.payment_mode
                ? MODE_LABEL[payment.payment_mode]
                : 'not recorded'}
          </p>
        </div>

        <PaymentModePicker mode={mode} onChange={setMode} />
        {mode === 'split' && (
          <SplitFields cashPart={cashPart} onCashPart={setCashPart} upiPart={upiPartValue} />
        )}

        <p className="text-xs text-slate-500">
          Only how the money arrived changes — the amount and what it paid for stay as they are.
          The drawer and UPI totals in reports follow the correction.
        </p>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            className="flex-1"
            onClick={save}
            disabled={busy || !splitValid || unchanged}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Hand changes to stored credit. Admin-only, with a reason on every change.
// "Give" is a refund or goodwill kept on account; "Take back" is usually the
// credit being handed back to the customer as cash. The recent history sits
// underneath, because "why do I have this credit?" is the follow-up question.
function CreditDialog({
  tab,
  onClose,
  onSaved,
}: {
  tab: Tab;
  onClose: () => void;
  onSaved: (tab: Tab) => void;
}) {
  const toast = useToast();
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<CreditAdjustment[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api.customers
      .credit(tab.customer_id, controller.signal)
      .then((result) => setHistory(result.adjustments))
      .catch(() => setHistory([]));
    return () => controller.abort();
  }, [tab.customer_id]);

  const magnitude = roundMoney(Number(amount));
  const delta = direction === 'add' ? magnitude : -magnitude;
  const after = roundMoney(tab.credit_balance + delta);
  const valid =
    Number.isFinite(magnitude) && magnitude > 0 && after >= 0 && reason.trim().length >= 3;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.customers.adjustCredit(tab.customer_id, {
        delta_amount: delta,
        reason: reason.trim(),
      });
      toast.success(
        direction === 'add'
          ? `${formatMoney(magnitude)} credit added for ${tab.customer_name}.`
          : `${formatMoney(magnitude)} credit taken back — hand it to ${tab.customer_name}.`,
      );
      onSaved(result.tab);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Adjust credit" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-slate-500">
            {tab.customer_name}'s credit
          </p>
          <p className="text-2xl font-semibold text-sky-300">{formatMoney(tab.credit_balance)}</p>
        </div>

        <div className="flex gap-2">
          {(['add', 'subtract'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDirection(option)}
              disabled={option === 'subtract' && tab.credit_balance <= 0}
              className={`min-h-11 flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                direction === option
                  ? option === 'add'
                    ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                    : 'border-rose-500/60 bg-rose-500/15 text-rose-300'
                  : 'border-slate-800 text-slate-400 hover:bg-slate-800'
              }`}
            >
              {option === 'add' ? 'Give credit' : 'Take back'}
            </button>
          ))}
        </div>

        <Input
          label="Amount (₹)"
          value={amount}
          onChange={(event) => setAmount(cleanMoney(event.target.value))}
          inputMode="decimal"
          placeholder={direction === 'subtract' ? String(tab.credit_balance) : '100'}
          required
        />
        {direction === 'subtract' && tab.credit_balance > 0 && (
          <Button
            type="button"
            variant="ghost"
            className="-mt-2 px-0 text-xs"
            onClick={() => setAmount(String(tab.credit_balance))}
          >
            All of it ({formatMoney(tab.credit_balance)})
          </Button>
        )}

        <Input
          label="Reason (recorded in the credit history)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={
            direction === 'add' ? 'PC-03 crashed, refund kept as credit' : 'Credit returned in cash'
          }
          required
        />

        {magnitude > 0 && (
          <p className="text-sm text-slate-400">
            {formatMoney(tab.credit_balance)} →{' '}
            <span className={after < 0 ? 'text-rose-300' : 'font-semibold text-slate-100'}>
              {after < 0 ? 'below zero' : formatMoney(after)}
            </span>
            {direction === 'subtract' && after >= 0 && (
              <span className="block text-xs text-amber-300">
                Hand {formatMoney(magnitude)} back to {tab.customer_name}. This is not counted in
                the drawer or reports.
              </span>
            )}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="flex-1" disabled={busy || !valid}>
            Save
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>

        <div className="border-t border-slate-800 pt-3">
          <p className="text-xs uppercase tracking-wide text-slate-500">Credit changes</p>
          {history === null ? (
            <p className="mt-1 text-sm text-slate-500">Loading…</p>
          ) : history.length === 0 ? (
            <p className="mt-1 text-sm text-slate-500">
              None yet. Overpayments kept as credit show under Payments.
            </p>
          ) : (
            <ul className="mt-1 max-h-48 divide-y divide-slate-800/60 overflow-y-auto">
              {history.map((row) => (
                <li key={row.adjustment_id} className="flex justify-between gap-3 py-1.5 text-sm">
                  <div className="min-w-0">
                    <p className="text-slate-300">{row.reason}</p>
                    <p className="text-xs text-slate-500">
                      {formatDateTime(row.created_at)}
                      {row.adjusted_by_name && ` · ${row.adjusted_by_name}`}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 font-semibold ${
                      row.delta_amount > 0 ? 'text-emerald-300' : 'text-rose-300'
                    }`}
                  >
                    {row.delta_amount > 0 ? '+' : '−'}
                    {formatMoney(Math.abs(row.delta_amount))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </Modal>
  );
}

function LineLabel({ line }: { line: TabLine }) {
  return (
    <div className="min-w-0">
      <p className="font-medium text-slate-100">
        {line.kind === 'recharge' && (
          <span className="mr-1.5 align-middle">
            <Badge tone="sky">Recharge</Badge>
          </span>
        )}
        {line.kind === 'ps5' && (
          <span className="mr-1.5 align-middle">
            <Badge tone="sky">PS5</Badge>
          </span>
        )}
        {line.kind === 'balance' && (
          <span className="mr-1.5 align-middle">
            <Badge tone="amber">Owed</Badge>
          </span>
        )}
        {line.item_name}
        {line.qty > 1 && (
          <span className="ml-3 rounded-md bg-slate-800 px-1.5 py-0.5 text-sm font-normal text-slate-300">
            ×{line.qty}
          </span>
        )}
      </p>
      <p className="text-xs text-slate-500">
        {line.recharge
          ? `${formatMinutes(line.recharge.minutes_purchased)} ${POOL_LABEL[line.recharge.pool]} · ${formatMinutes(line.recharge.minutes_remaining)} left · `
          : `${formatMoney(line.selling_price)} each · `}
        {line.ps5?.is_extension && !line.payment_id &&
          (line.ps5.started ? 'playing on it · ' : 'queued · ')}
        added {formatDateTime(line.added_at)}
        {line.added_by_name && ` by ${line.added_by_name}`}
      </p>
    </div>
  );
}

function LineRow({
  line,
  busy,
  isAdmin,
  onRemove,
  onPay,
}: {
  line: TabLine;
  busy: boolean;
  isAdmin: boolean;
  onRemove: (qty?: number) => void;
  onPay: () => void;
}) {
  const recharge = line.kind === 'recharge';
  // Cancelling a recharge takes the minutes back too, so it is an admin
  // decision — and only while the pack is untouched. Staff see no button.
  const canCancel = recharge && isAdmin && line.recharge
    ? line.recharge.minutes_remaining >= line.recharge.minutes_purchased
    : false;
  // A PS5 session is not refunded. Only an admin can take its charge off, for
  // a session started by mistake. Time added mid-session can be taken off
  // only until the group starts playing on it.
  const ps5 = line.kind === 'ps5';
  const ps5Locked = ps5 && !!line.ps5?.is_extension && line.ps5.started;
  // A balance owed from a short payment: removing it forgives the debt, so
  // that is an admin's call too.
  const balance = line.kind === 'balance';
  const removable = recharge ? canCancel : ps5 ? isAdmin && !ps5Locked : balance ? isAdmin : true;

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <LineLabel line={line} />
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-100">{formatMoney(line.line_total)}</span>
        {/* One unit at a time when the quantity is above one, so a "three
            Cokes, actually two" correction is one tap and not a re-add. */}
        {!recharge && !ps5 && line.qty > 1 && (
          <Button
            variant="ghost"
            className="ml-1 min-h-8 rounded-md border border-slate-700/80 bg-slate-800/60 px-2.5 py-1 text-xs font-semibold text-slate-200 backdrop-blur-sm hover:bg-slate-700/70"
            disabled={busy}
            onClick={() => onRemove(1)}
            aria-label={`Remove one ${line.item_name}`}
          >
            −1
          </Button>
        )}
        {removable && (
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
            disabled={busy}
            onClick={() => onRemove()}
          >
            {recharge ? 'Cancel' : balance ? 'Write off' : 'Remove'}
          </Button>
        )}
        {/* Settle just this line — the customer paying for their drink now and
            leaving the rest on the tab. */}
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs text-emerald-300 hover:text-emerald-200"
          disabled={busy}
          onClick={onPay}
        >
          Paid
        </Button>
      </div>
    </li>
  );
}

// The customer's tab: what they owe, line by line — food, drinks and
// recharges — with the controls to add, take off, and settle. The due figure
// is always what the server computed from the selling price of the unpaid
// lines.
export function CustomerTabSection({
  customerId,
  customerName,
  isActive,
  onChanged,
  refreshKey = 0,
}: {
  customerId: string;
  customerName: string;
  isActive: boolean;
  /** Fired after any write so the parent card can refresh its summary. */
  onChanged: () => void;
  /** Bumped by the parent when something OUTSIDE this section changed the
   *  tab — a recharge sold from the page header, an admin adjustment — so
   *  the line list refetches instead of showing what it loaded on mount. */
  refreshKey?: number;
}) {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The lines being settled in the confirmation: all of them, or one.
  const [paying, setPaying] = useState<TabLine[] | null>(null);
  const [keepingOpen, setKeepingOpen] = useState(false);
  const [adjustingCredit, setAdjustingCredit] = useState(false);
  const [changingMode, setChangingMode] = useState<TabPayment | null>(null);
  const [busyLine, setBusyLine] = useState<string | null>(null);
  // Any write in the app — an order accepted from the header bar lands on
  // this tab without passing through this component.
  const version = useDataVersion();

  const load = useCallback(async () => {
    try {
      setTab(await api.customers.tab(customerId));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey, version]);

  const applyTab = (next: Tab) => {
    setTab(next);
    onChanged();
  };

  const remove = async (line: TabLine, qty?: number) => {
    setBusyLine(line.tab_item_id);
    try {
      const next = await api.customers.removeFromTab(customerId, line.tab_item_id, qty);
      if (line.kind === 'recharge') toast.info(`${line.item_name} cancelled — the minutes are gone.`);
      applyTab(next);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyLine(null);
    }
  };

  const unpaidCount = tab?.unpaid.reduce((sum, line) => sum + line.qty, 0) ?? 0;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
          Tab
        </h2>
        <div className="flex flex-wrap gap-2">
          {/* A deactivated account cannot take new orders — the backend refuses
              with a 409 anyway, so the button says so up front. */}
          <Button
            variant="secondary"
            disabled={!isActive}
            onClick={() => setAdding(true)}
            title={isActive ? undefined : 'Reactivate the account to add items'}
          >
            Add item
          </Button>
          {/* Admin-only; the backend refuses staff with a 403 as well. */}
          {tab && isAdmin && (
            <Button variant="secondary" onClick={() => setAdjustingCredit(true)}>
              Credit
            </Button>
          )}
          {tab && tab.unpaid.length > 0 && (
            <Button variant="secondary" onClick={() => setKeepingOpen(true)}>
              {tab.tab_open_until ? 'Change date' : 'Keep open'}
            </Button>
          )}
          {tab && tab.unpaid.length > 0 && (
            <Button onClick={() => setPaying(tab.unpaid)}>Mark all paid</Button>
          )}
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {tab === null && !error ? (
        <Spinner label="Loading tab…" />
      ) : tab && (
        <>
          <Card
            className={
              tab.overdue
                ? 'border-rose-500/50 bg-rose-500/10'
                : tab.due_amount > 0
                  ? 'border-amber-500/50 bg-amber-500/10'
                  : undefined
            }
          >
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Due now</p>
                <p
                  className={`mt-1 text-3xl font-semibold ${
                    tab.overdue
                      ? 'text-rose-300'
                      : tab.due_amount > 0
                        ? 'text-amber-300'
                        : 'text-slate-500'
                  }`}
                >
                  {formatMoney(owed(tab))}
                </p>
                {/* Money already handed over against the open lines — a
                    part-payment's remainder — is taken off the big figure. */}
                {tab.credit_balance > 0 && tab.due_amount > 0 && (
                  <p className="mt-1 text-xs text-slate-400">
                    {formatMoney(tab.due_amount)} on the tab −{' '}
                    {formatMoney(Math.min(tab.credit_balance, tab.due_amount))} from credit
                  </p>
                )}
                {tab.due_amount > 0 && owed(tab) === 0 && (
                  <p className="mt-1 text-xs text-sky-300">
                    Covered by credit — settle the tab to use it.
                  </p>
                )}
                {/* The settle-by date and the credit both belong next to the
                    figure: they are the two things that change what "due"
                    means when the customer is standing at the counter. */}
                {tab.tab_open_until && tab.due_amount > 0 && (
                  <p className={`mt-1 text-xs ${tab.overdue ? 'text-rose-300' : 'text-slate-400'}`}>
                    {tab.overdue ? 'Was due by' : 'Open until'} {formatDate(tab.tab_open_until)}
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                {tab.overdue ? (
                  <Badge tone="red">Overdue</Badge>
                ) : tab.due_amount > 0 ? (
                  <Badge tone="amber">
                    {unpaidCount} unpaid item{unpaidCount === 1 ? '' : 's'}
                  </Badge>
                ) : (
                  <Badge tone="green">Nothing owed</Badge>
                )}
                {tab.credit_balance > 0 && (
                  <Badge tone="sky">{creditLabel(tab.credit_balance, tab.due_amount)}</Badge>
                )}
              </div>
            </div>
          </Card>

          {tab.unpaid.length > 0 && (
            <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
              {tab.unpaid.map((line) => (
                <LineRow
                  key={line.tab_item_id}
                  line={line}
                  busy={busyLine === line.tab_item_id}
                  isAdmin={isAdmin}
                  onRemove={(qty) => remove(line, qty)}
                  onPay={() => setPaying([line])}
                />
              ))}
            </ul>
          )}

          {/* The receipt: every settlement, newest first, with what it covered
              and how it was paid. */}
          {tab.payments.length > 0 && (
            <details className="rounded-xl border border-slate-800 px-4 py-3">
              <summary className="cursor-pointer text-sm text-slate-400">
                Payments · {tab.payments.length} confirmed
              </summary>
              <ul className="mt-2 divide-y divide-slate-800/60">
                {tab.payments.map((payment) => (
                  <li key={payment.payment_id} className="py-2 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-slate-200">
                          {payment.items.length === 0 && 'Part-payment towards the tab'}
                          {payment.items.map((line, index) => (
                            <span key={line.tab_item_id}>
                              {index > 0 && ', '}
                              {line.kind === 'recharge' && (
                                <span className="text-sky-400">Recharge </span>
                              )}
                              {line.kind === 'ps5' && (
                                <span className="text-violet-400">PS5 </span>
                              )}
                              {line.kind === 'balance' && (
                                <span className="text-amber-400">Owed </span>
                              )}
                              {line.item_name}
                              {line.qty > 1 && ` ×${line.qty}`}
                            </span>
                          ))}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {formatDateTime(payment.paid_at)}
                          {payment.received_by_name && ` · ${payment.received_by_name}`}
                          {payment.payment_mode === 'split'
                            ? ` · ${formatMoney(payment.cash_amount)} cash + ${formatMoney(payment.upi_amount)} UPI`
                            : payment.payment_mode
                              ? ` · ${payment.payment_mode.toUpperCase()}`
                              : ''}
                          {payment.credit_applied > 0 &&
                            ` · ${formatMoney(payment.credit_applied)} from credit`}
                          {payment.credit_added > 0 &&
                            ` · ${formatMoney(payment.credit_added)} kept as credit`}
                          {(payment.balance_carried ?? 0) > 0 &&
                            ` · ${formatMoney(payment.balance_carried ?? 0)} left owed`}
                        </p>
                        {payment.mode_changed_at && (
                          <p className="text-xs text-amber-300/80">
                            Mode changed
                            {payment.mode_changed_from &&
                              ` from ${MODE_LABEL[payment.mode_changed_from]}`}
                            {payment.mode_changed_by_name && ` by ${payment.mode_changed_by_name}`} ·{' '}
                            {formatDateTime(payment.mode_changed_at)}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        {/* A part-payment that covered no line has amount 0;
                            what was handed over is the figure that matters. */}
                        <p className="font-semibold text-slate-100">
                          {formatMoney(payment.items.length === 0 ? payment.amount_received : payment.amount)}
                        </p>
                        {payment.items.length > 0 && payment.amount_received !== payment.amount && (
                          <p className="text-xs text-slate-500">
                            {formatMoney(payment.amount_received)} received
                          </p>
                        )}
                        {/* Admin-only; the backend refuses staff with a 403 too.
                            Nothing to correct when no money changed hands. */}
                        {isAdmin && payment.amount_received > 0 && (
                          <Button
                            variant="ghost"
                            className="px-2 py-1 text-xs"
                            onClick={() => setChangingMode(payment)}
                          >
                            Change mode
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}

      {adding && (
        <AddItemDialog
          customerId={customerId}
          customerName={customerName}
          onClose={() => setAdding(false)}
          onAdded={applyTab}
        />
      )}
      {paying && tab && (
        <PayDialog
          tab={tab}
          lines={paying}
          onClose={() => setPaying(null)}
          onPaid={applyTab}
        />
      )}
      {changingMode && tab && (
        <ChangeModeDialog
          tab={tab}
          payment={changingMode}
          onClose={() => setChangingMode(null)}
          onSaved={applyTab}
        />
      )}
      {adjustingCredit && tab && (
        <CreditDialog
          tab={tab}
          onClose={() => setAdjustingCredit(false)}
          onSaved={applyTab}
        />
      )}
      {keepingOpen && tab && (
        <KeepOpenDialog
          tab={tab}
          onClose={() => setKeepingOpen(false)}
          onSaved={applyTab}
        />
      )}
    </section>
  );
}
